<template>
  <div class="product-management">
    <h2>商城商品管理</h2>

    <!-- 搜索栏 -->
    <el-form :inline="true" :model="searchForm" class="search-bar">
      <el-form-item label="商品名称">
        <el-input v-model="searchForm.name" placeholder="输入名称" clearable />
      </el-form-item>
      <el-form-item label="分类">
        <el-select v-model="searchForm.type" placeholder="全部分类" clearable style="width:160px">
          <el-option v-for="t in typeList" :key="t.id" :label="t.name" :value="t.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="searchForm.status" placeholder="全部" clearable style="width:100px">
          <el-option label="上架" :value="1" />
          <el-option label="下架" :value="0" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="loadProducts">查询</el-button>
        <el-button type="success" @click="openDialog()">新增商品</el-button>
      </el-form-item>
    </el-form>

    <!-- 商品表格 -->
    <el-table :data="productList" border stripe v-loading="loading" style="width:100%">
      <el-table-column type="index" label="序号" width="60" />
      <el-table-column label="图片" width="80">
        <template #default="{ row }">
          <el-image
            v-if="firstImage(row.images)"
            :src="firstImage(row.images)"
            :preview-src-list="[firstImage(row.images)]"
            :preview-teleported="true"
            style="width:50px;height:50px;cursor:pointer"
            fit="cover"
            :initial-index="0"
          />
          <span v-else>无图</span>
        </template>
      </el-table-column>
      <el-table-column prop="name" label="商品名称" min-width="140" />
      <el-table-column label="分类" width="80">
        <template #default="{ row }">{{ getTypeName(row.type) }}</template>
      </el-table-column>
      <el-table-column prop="price" label="单价(元)" width="90" />
      <el-table-column prop="stock" label="库存" width="70" />
      <el-table-column prop="sales" label="销量" width="70" />
      <el-table-column prop="unit" label="单位" width="60" />
      <el-table-column label="状态" width="70">
        <template #default="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'info'">{{ row.status === 1 ? '上架' : '下架' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{ row }">
          <el-button size="small" @click="openDialog(row)">编辑</el-button>
          <el-button size="small" :type="row.status === 1 ? 'warning' : 'success'" @click="toggleStatus(row)">
            {{ row.status === 1 ? '下架' : '上架' }}
          </el-button>
          <el-popconfirm title="确定删除？" @confirm="handleDelete(row.id)">
            <template #reference>
              <el-button size="small" type="danger">删除</el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
   <div class="pagination">
     <el-pagination background @size-change="handleSizeChange" @current-change="handlePageChange"
       :current-page="page" :page-size="size" :total="total"
       :page-sizes="[20, 50, 100]" layout="total, sizes, prev, pager, next, jumper" />
   </div>

    <!-- 新建分类弹窗 -->
    <el-dialog title="新建分类" v-model="typeDialogVisible" width="360px">
      <el-form label-width="80px">
        <el-form-item label="分类名称" required>
          <el-input v-model="newTypeName" maxlength="15" show-word-limit placeholder="最多15字符" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="typeDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="createType">确定</el-button>
      </template>
    </el-dialog>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialogTitle" v-model="dialogVisible" width="600px">
      <el-form :model="form" label-width="100px">
        <el-form-item label="商品名称" required>
          <el-input v-model="form.name" maxlength="30" show-word-limit />
        </el-form-item>
        <el-form-item label="分类" required>
          <div style="display:flex;gap:8px">
            <el-select v-model="form.type" placeholder="请选择" style="min-width:180px;flex:1">
              <el-option v-for="t in typeList" :key="t.id" :label="t.name" :value="t.id" />
            </el-select>
            <el-button @click="showTypeDialog">新建分类</el-button>
          </div>
        </el-form-item>
        <el-form-item label="图片URL">
          <div>
            <el-upload
              ref="uploadRef"
              name="files"
              :action="uploadUrl"
              :headers="uploadHeaders"
              :file-list="fileList"
              list-type="picture-card"
              :on-success="onUploadSuccess"
              :on-remove="onUploadRemove"
              :on-change="onFileChange"
              :before-upload="beforeUpload"
              accept="image/*"
            >
              <el-icon><Plus /></el-icon>
            </el-upload>
            <p style="color:#999;font-size:12px;margin-top:4px">仅支持单张图片，不超过2MB；超过2MB将提示压缩</p>
          </div>
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.desc" type="textarea" :rows="2" maxlength="200" show-word-limit />
        </el-form-item>
        <el-form-item label="单价(元)" required>
          <el-input-number v-model="form.price" :min="0" :precision="2" />
        </el-form-item>
        <el-form-item label="库存" required>
          <el-input-number v-model="form.stock" :min="0" />
        </el-form-item>
        <el-form-item label="单位">
          <el-select v-model="form.unit" placeholder="请选择" style="width:100px">
            <el-option label="盒" value="盒" />
            <el-option label="瓶" value="瓶" />
            <el-option label="套" value="套" />
            <el-option label="台" value="台" />
            <el-option label="包" value="包" />
            <el-option label="桶" value="桶" />
            <el-option label="个" value="个" />
          </el-select>
        </el-form-item>
        <el-form-item label="赠送积分">
          <el-input-number v-model="form.giveawayIntegral" :min="0" />
        </el-form-item>
        <el-form-item label="上架">
          <el-switch v-model="form.statusBool" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave">保存</el-button>
      </template>
    </el-dialog>

    <!-- 图片裁剪弹窗 -->
    <el-dialog title="裁剪图片（1:1 正方形，拖拽裁剪框调整位置）" v-model="cropVisible" width="520px" :close-on-click-modal="false" @closed="cleanupCropListeners">
      <div style="display:flex;flex-direction:column;align-items:center;gap:10px">
        <div class="crop-stage" ref="cropStage"
             @wheel.prevent="cropWheel"
             @mousedown="stageMouseDown"
             style="width:400px;height:400px;overflow:hidden;position:relative;background:#333;border-radius:4px;user-select:none">
          <img :src="cropImageSrc" ref="cropImageRef"
               :style="cropImageStyle"
               style="position:absolute;top:0;left:0;pointer-events:none" />
          <!-- 裁剪框：直接处理拖拽事件，九宫格子元素 pointer-events:none -->
          <div class="crop-mask" ref="cropMaskRef"
               :style="cropMaskStyle"
               @mousedown.stop="frameMouseDown">
            <!-- 九宫格辅助线 -->
            <div class="crop-grid-h" style="top:33.33%"></div>
            <div class="crop-grid-h" style="top:66.67%"></div>
            <div class="crop-grid-v" style="left:33.33%"></div>
            <div class="crop-grid-v" style="left:66.67%"></div>
          </div>
        </div>
        <!-- 缩放控制 -->
        <div style="display:flex;align-items:center;gap:8px">
          <el-button size="small" circle @click="zoomOut" :disabled="cropScale <= 0.1">&minus;</el-button>
          <span style="font-size:12px;color:#666;min-width:44px;text-align:center">{{ Math.round(cropScale * 100) }}%</span>
          <el-button size="small" circle @click="zoomIn" :disabled="cropScale >= 5">+</el-button>
          <el-button size="small" @click="resetCrop">重置</el-button>
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer" style="display:flex;justify-content:center;width:100%">
          <el-button type="primary" @click="confirmCrop">裁剪并上传</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref } from 'vue';
import { Plus } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { listProducts, saveProduct, updateProduct, updateProductStatus, deleteProduct, listProductTypes, saveProductType } from '@/api/mall';
import { API_BASE_URL } from '@/config';

export default {
  name: 'ProductManagement',
  components: { Plus },
  data() {
    return {
      searchForm: { name: '', type: null, status: null },
      productList: [],
      typeList: [],
      loading: false,
      page: 1,
      size: 20,
      total: 0,
      dialogVisible: false,
      editingId: null,
      form: { name: '', type: null, images: '', desc: '', price: 0, stock: 0, unit: '盒', giveawayIntegral: 0, statusBool: true },
      uploadUrl: API_BASE_URL + '/api/common/upload/image',
      fileList: [],
      typeDialogVisible: false,
      newTypeName: '',
      // 裁剪相关
      cropVisible: false,
      cropImageSrc: '',
      cropScale: 1,
      cropPanX: 0,
      cropPanY: 0,
      cropFrame: { x: 100, y: 100, w: 200, h: 200 },
      cropStageSize: 400,
      cropOriginalFile: null,
    };
  },
  computed: {
    dialogTitle() { return this.editingId ? '编辑商品' : '新增商品'; },
    uploadHeaders() {
      const token = localStorage.getItem('token');
      return token ? { token } : {};
    },
    cropImageStyle() {
      if (!this.cropImageSrc) return { display: 'none' };
      return {
        transform: `translate(${this.cropPanX}px, ${this.cropPanY}px) scale(${this.cropScale})`,
        transformOrigin: '0 0',
      };
    },
    cropMaskStyle() {
      const f = this.cropFrame;
      return {
        left: f.x + 'px',
        top: f.y + 'px',
        width: f.w + 'px',
        height: f.h + 'px',
        position: 'absolute',
        outline: '2px solid #fff',
        boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
        cursor: 'move',
        boxSizing: 'border-box',
        background: 'transparent',
        zIndex: 5,
      };
    },
  },
  mounted() {
    this.loadTypes();
    this.loadProducts();
  },
  methods: {
    // beforeUpload 返回 false 时 el-upload 仍会将文件加入内部 uploadFiles 并生成 blob URL。
    // 在 on-change 中按 uid 精确移除，避免 ERR_FILE_NOT_FOUND。
    onFileChange(file) {
      if (file.url && String(file.url).startsWith('blob:')) {
        const uploadRef = this.$refs.uploadRef;
        if (uploadRef && uploadRef.uploadFiles) {
          const idx = uploadRef.uploadFiles.findIndex(f => f.uid === file.uid);
          if (idx !== -1) {
            uploadRef.uploadFiles.splice(idx, 1);
          }
        }
      }
    },
    async loadTypes() {
      try {
        const res = await listProductTypes();
        this.typeList = res.data || [];
      } catch (e) { console.error(e); }
    },
   handlePageChange(page) {
     this.page = page;
     this.loadProducts();
   },
   async loadProducts() {
     this.loading = true;
     try {
       const res = await listProducts({ page: this.page, size: this.size, ...this.searchForm });
       const pageData = res.data || res;
       this.productList = pageData.records || [];
       this.total = pageData.total || 0;
     } catch (e) { console.error(e); }
     finally { this.loading = false; }
   },
   handleSizeChange(size) {
     this.size = size;
     this.page = 1;
     this.loadProducts();
   },
   getTypeName(typeId) {
      const t = this.typeList.find(t => t.id === typeId);
      return t ? t.name : '';
    },
    firstImage(images) {
      // 单图片场景：images 已是完整 URL 或空字符串
      if (!images) return null;
      return images.trim() || null;
    },
    openDialog(row) {
      if (row) {
        this.editingId = row.id;
        this.fileList = [];
        if (row.images && row.images.trim()) {
          this.fileList = [{
            uid: 0,
            name: 'image-0',
            url: row.images.trim(),
            status: 'success',
          }];
        }
        this.form = {
          name: row.name, type: row.type, images: row.images || '', desc: row.desc || '',
          price: row.price, stock: row.stock, unit: row.unit, giveawayIntegral: row.giveawayIntegral || 0,
          statusBool: row.status === 1,
        };
      } else {
        this.editingId = null;
        this.fileList = [];
        this.form = { name: '', type: null, images: '', desc: '', price: 0, stock: 0, unit: '盒', giveawayIntegral: 0, statusBool: true };
      }
      this.dialogVisible = true;
    },
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/');
      if (!isImage) { ElMessage.error('只能上传图片文件'); return false; }
      // 走裁剪流程，阻止默认上传
      this.initCrop(file);
      return false;
    },
    // ---- 裁剪 begin ----
    initCrop(file) {
      this.cropOriginalFile = file;
      this.cleanupCropListeners();
      const reader = new FileReader();
      reader.onload = (e) => {
        this.cropImageSrc = e.target.result;
        const img = new Image();
        img.onload = () => {
          this._doReset(img.width, img.height);
          this.cropVisible = true;
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    // 拖拽裁剪框（直接由 crop-mask 的 @mousedown.stop 触发）
    frameMouseDown(e) {
      e.preventDefault();
      const stageRect = this.$refs.cropStage.getBoundingClientRect();
      const f = this.cropFrame;
      const ox = e.clientX - stageRect.left - f.x;
      const oy = e.clientY - stageRect.top - f.y;
      const maxX = this.cropStageSize - f.w;
      const maxY = this.cropStageSize - f.h;
      const onMove = (ev) => {
        ev.preventDefault();
        const rect = this.$refs.cropStage.getBoundingClientRect();
        const nx = Math.max(0, Math.min(maxX, ev.clientX - rect.left - ox));
        const ny = Math.max(0, Math.min(maxY, ev.clientY - rect.top - oy));
        this.cropFrame = { ...this.cropFrame, x: nx, y: ny };
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },

    // 拖拽背景图片（点击裁剪框外部时触发）
    stageMouseDown(e) {
      const rect = this.$refs.cropStage.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const f = this.cropFrame;
      // 如果点击在裁剪框内，由 frameMouseDown 处理（已 stop 冒泡），这里做兜底
      if (mx >= f.x && mx <= f.x + f.w && my >= f.y && my <= f.y + f.h) return;
      e.preventDefault();
      const px = this.cropPanX, py = this.cropPanY;
      const sx = e.clientX, sy = e.clientY;
      const onMove = (ev) => {
        this.cropPanX = px + ev.clientX - sx;
        this.cropPanY = py + ev.clientY - sy;
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },

    zoomIn() { this.applyZoom(0.1); },
    zoomOut() { this.applyZoom(-0.1); },
    applyZoom(delta) {
      const ns = Math.max(0.1, Math.min(5, this.cropScale + delta));
      if (ns === this.cropScale) return;
      const c = this.cropStageSize / 2;
      const rx = (c - this.cropPanX) / this.cropScale;
      const ry = (c - this.cropPanY) / this.cropScale;
      this.cropScale = ns;
      this.cropPanX = c - rx * ns;
      this.cropPanY = c - ry * ns;
    },
    cropWheel(e) {
      const d = e.deltaY > 0 ? -0.05 : 0.05;
      const ns = Math.max(0.1, Math.min(5, this.cropScale + d));
      if (ns === this.cropScale) return;
      const rect = this.$refs.cropStage.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const rx = (mx - this.cropPanX) / this.cropScale;
      const ry = (my - this.cropPanY) / this.cropScale;
      this.cropScale = ns;
      this.cropPanX = mx - rx * ns;
      this.cropPanY = my - ry * ns;
    },
    resetCrop() {
      if (!this.cropImageSrc) return;
      // 通过 ref 获取已渲染图片的自然尺寸
      const imgEl = this.$refs.cropImageRef;
      if (!imgEl || !imgEl.naturalWidth) {
        // 回退：新建 Image 读取 data URL
        const img = new Image();
        img.onload = () => {
          this._doReset(img.width, img.height);
        };
        img.src = this.cropImageSrc;
        return;
      }
      this._doReset(imgEl.naturalWidth, imgEl.naturalHeight);
    },
    _doReset(nw, nh) {
      const s = this.cropStageSize;
      const fitScale = Math.min(s / nw, s / nh, 1);
      this.cropScale = fitScale;
      const iw = nw * fitScale;
      const ih = nh * fitScale;
      this.cropPanX = (s - iw) / 2;
      this.cropPanY = (s - ih) / 2;
      const size = Math.min(iw, ih, s) * 0.85;
      this.cropFrame = { x: (s - size) / 2, y: (s - size) / 2, w: size, h: size };
    },

    confirmCrop() {
      if (!this.cropImageSrc) { this.cropVisible = false; return; }
      const f = this.cropFrame;
      const sx = (f.x - this.cropPanX) / this.cropScale;
      const sy = (f.y - this.cropPanY) / this.cropScale;
      const sw = f.w / this.cropScale;
      const sh = f.h / this.cropScale;

      const img = new Image();
      img.onload = () => {
        // 正确裁剪：先算右/下边界，再夹紧左/上边界，然后算宽高
        const right = Math.min(sx + sw, img.width);
        const bottom = Math.min(sy + sh, img.height);
        const clipX = Math.max(0, sx);
        const clipY = Math.max(0, sy);
        const clipW = Math.max(1, right - clipX);
        const clipH = Math.max(1, bottom - clipY);

        const canvas = document.createElement('canvas');
        canvas.width = clipW;
        canvas.height = clipH;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, clipX, clipY, clipW, clipH, 0, 0, clipW, clipH);

        canvas.toBlob((blob) => {
          if (!blob) { this.cropVisible = false; ElMessage.error('裁剪失败'); return; }
          const croppedFile = new File([blob], 'cropped_' + Date.now() + '.jpg', { type: 'image/jpeg' });
          this.cropVisible = false;
          this.uploadCroppedFile(croppedFile);
        }, 'image/jpeg', 0.9);
      };
      img.src = this.cropImageSrc;
    },

    cleanupCropListeners() {
    },
    // 上传裁剪后的文件（绕过 el-upload 的 before-upload，直接 fetch）
    uploadCroppedFile(file) {
      const sizeMB = file.size / 1024 / 1024;
      const doUpload = (f) => {
        const formData = new FormData();
        formData.append('files', f, f.name || 'cropped.jpg');
        const token = localStorage.getItem('token') || '';
        const headers = {};
        if (token) headers.token = token;
        fetch(this.uploadUrl, { method: 'POST', headers, body: formData })
          .then(res => {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.json();
          })
          .then(data => {
            if (data.code === 200 && data.data && data.data.length > 0) {
              // 单图片：只取第一张上传成功的 URL
              this.form.images = data.data[0];
              // 单图片：替换 fileList 为仅当前上传的图片
              this.fileList = data.data.map(url => ({
                name: url.split('/').pop() || 'image',
                url,
                status: 'success'
              }));
              ElMessage.success('上传成功');
            } else {
              ElMessage.error(data.msg || '上传失败');
            }
          })
          .catch((e) => {
            console.error('Upload error:', e);
            ElMessage.error('上传失败，请重试');
          });
      };

      if (sizeMB <= 2) {
        doUpload(file);
      } else {
        ElMessageBox.confirm(
          `裁剪后图片 ${sizeMB.toFixed(1)}MB，超过2MB限制。是否自动压缩后上传？`,
          '图片过大',
          { confirmButtonText: '压缩上传', cancelButtonText: '取消', type: 'warning' }
        ).then(() => {
          this.compressImage(file).then(compressed => doUpload(compressed))
            .catch(() => ElMessage.error('压缩失败'));
        }).catch(() => {});
      }
    },
    // ---- 裁剪 end ----
    checkAndCompress(file) {
      const sizeMB = file.size / 1024 / 1024;
      if (sizeMB <= 2) {
        if (file.type === 'image/avif') {
          ElMessage.warning('AVIF格式暂不支持直传，请转换为JPG/PNG后上传');
          return false;
        }
        return true;
      }
      if (file.type === 'image/avif') {
        ElMessage.warning('AVIF格式不支持在线压缩，请转换为JPG/PNG后上传');
        return false;
      }
      return new Promise((resolve) => {
        ElMessageBox.confirm(
          `图片大小 ${sizeMB.toFixed(1)}MB，超过2MB限制。是否自动压缩后上传？`,
          '图片过大',
          { confirmButtonText: '压缩上传', cancelButtonText: '取消', type: 'warning' }
        ).then(() => {
          this.compressImage(file).then(resolve).catch(() => { ElMessage.error('压缩失败'); resolve(false); });
        }).catch(() => resolve(false));
      });
    },
    compressImage(file) {
      const type = file.type;
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            let { width, height } = img;
            const maxW = 1200, maxH = 1200;
            if (width > maxW || height > maxH) {
              const ratio = Math.min(maxW / width, maxH / height);
              width = Math.round(width * ratio);
              height = Math.round(height * ratio);
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
              if (!blob) { reject(new Error('压缩失败')); return; }
              const compressed = new File([blob], file.name, { type, lastModified: Date.now() });
              ElMessage.success(`已压缩至 ${(compressed.size / 1024 / 1024).toFixed(1)}MB`);
              resolve(compressed);
            }, type, 0.7);
          };
          img.onerror = () => reject(new Error('图片加载失败'));
          img.src = e.target.result;
        };
        reader.onerror = () => reject(new Error('读取文件失败'));
        reader.readAsDataURL(file);
      });
    },
    onUploadSuccess(response) {
      if (response.code === 200 && response.data && response.data.length > 0) {
        // 单图片：只取第一张上传成功的 URL
        this.form.images = response.data[0];
      } else {
        ElMessage.error(response.msg || '上传失败');
      }
    },
    onUploadRemove(file) {
      // 单图片：移除时清空
      this.form.images = '';
      this.fileList = [];
    },
    async handleSave() {
      const data = {
        ...this.form,
        status: this.form.statusBool ? 1 : 0,
      };
      try {
        if (this.editingId) {
          data.id = this.editingId;
          await updateProduct(data);
          this.$message.success('更新成功');
        } else {
          await saveProduct(data);
          this.$message.success('新增成功');
        }
        this.dialogVisible = false;
        this.loadProducts();
      } catch (e) { this.$message.error('操作失败'); }
    },
    async toggleStatus(row) {
      const newStatus = row.status === 1 ? 0 : 1;
      try {
        await updateProductStatus(row.id, newStatus);
        this.$message.success(newStatus === 1 ? '已上架' : '已下架');
        this.loadProducts();
      } catch (e) { this.$message.error('操作失败'); }
    },
    async handleDelete(id) {
      try {
        await deleteProduct(id);
        this.$message.success('已删除');
        this.loadProducts();
      } catch (e) { this.$message.error('删除失败'); }
    },
    showTypeDialog() {
      this.newTypeName = '';
      this.typeDialogVisible = true;
    },
    async createType() {
      const name = this.newTypeName.trim();
      if (!name) { this.$message.warning('请输入分类名称'); return; }
      if (name.length > 15) { this.$message.warning('分类名称不能超过15字符'); return; }
      try {
        await saveProductType({ name });
        this.$message.success('新建分类成功');
        this.typeDialogVisible = false;
        await this.loadTypes();
      } catch (e) { this.$message.error('创建失败，名称可能已存在'); }
    },
  },
};
</script>

<style scoped>
.product-management { padding: 0; }
.search-bar { background: #fff; padding: 16px; border-radius: 4px; margin-bottom: 16px; }
.pagination { margin-top: 16px; display: flex; justify-content: flex-end; }

/* 裁剪组件样式 */
.crop-mask {
  position: absolute;
  outline: 2px solid #fff;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55);
  cursor: move;
  box-sizing: border-box;
  z-index: 5;
}
/* 九宫格辅助线 */
.crop-grid-h {
  position: absolute;
  left: 0;
  right: 0;
  height: 0;
  border-top: 1px dashed rgba(255, 255, 255, 0.45);
  pointer-events: none;
  z-index: 1;
}
.crop-grid-v {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 1px dashed rgba(255, 255, 255, 0.45);
  pointer-events: none;
  z-index: 1;
}
</style>
